import { getAPIURL } from "./config";

/**
 * Вспомогательная функция для обработки нулевых значений
 * Сохраняет 0 если сервер вернул 0, заменяет только null/undefined
 */
const safeNumber = (value, defaultValue = 0) => {
  if (value === null || value === undefined) {
    return defaultValue;
  }
  return value;
};

/**
 * ОБНОВЛЕННЫЙ API ДЛЯ РАБОТЫ СО СМЕТОЙ:
 *
 * 1. Добавлена функция getProjectEstimate() - основной эндпоинт для получения всех данных сметы
 * 2. Созданы функции createEstimateContext() и getSectionContext() для правильного сбора контекста
 * 3. Удалены все хардкодированные значения (400000, 30, 4, 5, 65, 50, 7, 1, 15 и т.д.)
 * 4. Функции теперь используют реальные данные из API вместо хардкода
 * 5. Сохранена обратная совместимость через legacy функции
 *
 * НОВЫЕ ФУНКЦИИ:
 * - getProjectEstimate(projectId) - получить все данные сметы
 * - createEstimateContext(projectId) - создать полный контекст для работы со сметой
 * - getSectionContext(projectId, sectionId) - получить контекст для конкретной секции
 *
 * ИСПОЛЬЗОВАНИЕ:
 * const context = await createEstimateContext(projectId);
 * const estimateData = context.estimate;
 * const sectionsData = context.sections;
 */

/** Порядок блоков: Паркинг, затем "Блок 1", "Блок 2", ... по возрастанию, затем остальные. */
const sortBlockExpendituresOrder = (blockExpenditures) => {
  if (!Array.isArray(blockExpenditures) || blockExpenditures.length === 0)
    return blockExpenditures;
  const blockRegex = /Блок\s*(\d+)/i;
  const isParking = (name) => (name || "").toLowerCase().trim() === "паркинг";
  const withMeta = blockExpenditures.map((item) => {
    const name = (item?.block?.name || "").trim();
    const parking = isParking(name);
    const m = name.match(blockRegex);
    const num = m ? parseInt(m[1], 10) : null;
    return { item, parking, num };
  });
  const parking = withMeta.filter((x) => x.parking).map((x) => x.item);
  const numbered = withMeta
    .filter((x) => !x.parking && x.num !== null)
    .sort((a, b) => a.num - b.num)
    .map((x) => x.item);
  const rest = withMeta
    .filter((x) => !x.parking && x.num === null)
    .map((x) => x.item);
  return [...parking, ...numbered, ...rest];
};

/**
 * Собрать карту деталей секций из construction_design_detailed (один GET сметы).
 * Формат как у getSectionConstructionDesign: bonus_percent, average_salary, block_expenditures, section_id.
 * Блоки в block_expenditures: Паркинг → Блок 1,2,… → остальные.
 */
export const buildSectionDetailsFromDetailed = (constructionDesignDetailed) => {
  const sectionsData = {};
  (constructionDesignDetailed || []).forEach((detail) => {
    const sectionId = detail.section_id;
    if (sectionId == null) return;
    const rawBlocks = detail.block_expenditures || [];
    sectionsData[sectionId] = {
      bonus_percent: detail.bonus_percent ?? 0,
      average_salary: detail.average_salary ?? 0,
      block_expenditures: sortBlockExpendituresOrder(rawBlocks),
      section_id: sectionId,
    };
  });
  return sectionsData;
};

// Вспомогательная функция для получения реальных данных секций (один GET сметы, без вызовов по секциям)
export const getRealSectionData = async (projectId, sections) => {
  try {
    const data = await getProjectEstimate(projectId);
    const fromDetailed = buildSectionDetailsFromDetailed(
      data.construction_design_detailed,
    );
    const realSectionData = {};
    for (const section of sections || []) {
      const sid = section.section?.id;
      if (sid == null) continue;
      realSectionData[sid] = fromDetailed[sid] || {
        bonus_percent: 0,
        average_salary: 0,
        block_expenditures: [],
        section_id: sid,
      };
    }
    return realSectionData;
  } catch (err) {
    console.error("Error fetching section data from estimate:", err);
    const realSectionData = {};
    for (const section of sections || []) {
      const sid = section.section?.id;
      if (sid != null) {
        realSectionData[sid] = {
          bonus_percent: 0,
          average_salary: 0,
          block_expenditures: [],
          section_id: sid,
        };
      }
    }
    return realSectionData;
  }
};

/**
 * Создать контекст для работы со сметой (загрузка сметы + деталей секций).
 * Использует GET /project_estimates/projects/{project_id} с опциональным ?version=
 * @param {number} projectId - ID проекта
 * @param {number|null} [version] - Опциональный номер версии сметы
 */
export const createEstimateContext = async (projectId, version = null) => {
  try {
    const estimateData = await getProjectEstimate(projectId, version);

    // Объединяем разделы из construction_design и construction_design_project
    const allSections = [
      ...(estimateData?.construction_design || []),
      ...(estimateData?.construction_design_project || []),
    ];

    console.log(
      "[createEstimateContext] Загружено разделов из estimateData:",
      allSections.length,
    );
    console.log(
      "[createEstimateContext] Разделы из construction_design:",
      estimateData?.construction_design?.length || 0,
    );
    console.log(
      "[createEstimateContext] Разделы из construction_design_project:",
      estimateData?.construction_design_project?.length || 0,
    );
    console.log(
      "[createEstimateContext] Разделы:",
      allSections.map((s) => ({
        id: s.section?.id,
        name: s.section?.name,
      })),
    );

    // Сохраняем список ID разделов для сравнения после сохранения
    const sectionIdsBefore = new Set(
      allSections.map((s) => s.section?.id).filter(Boolean),
    );
    console.log(
      "[createEstimateContext] ID разделов до сохранения:",
      Array.from(sectionIdsBefore).sort((a, b) => a - b),
    );

    // Детали секций: из одного GET (construction_design_detailed), без вызовов GET /sections/{id}
    let sectionsData = buildSectionDetailsFromDetailed(
      estimateData.construction_design_detailed,
    );

    // Обогащаем sectionsData названиями разделов из allSections и дополняем отсутствующие разделы
    for (const section of allSections) {
      const sectionId = section.section?.id;
      if (!sectionId) continue;
      const sectionNameRaw = section.section?.name;
      const sectionName = sectionNameRaw?.toLowerCase().trim();
      const isUnified = sectionName === "инсоляция" || sectionName === "гп";

      if (sectionsData[sectionId]) {
        // Раздел уже есть — добавляем название, если его нет
        if (!sectionsData[sectionId].section_name) {
          sectionsData[sectionId] = {
            ...sectionsData[sectionId],
            section_name: sectionNameRaw,
          };
        }
      } else {
        // Раздела нет в detailed (единые разделы ГП, Инсоляция и т.д.) — создаём
        sectionsData = {
          ...sectionsData,
          [sectionId]: {
            bonus_percent: 0,
            average_salary: 0,
            block_expenditures: [],
            section_id: sectionId,
            section_name: sectionNameRaw,
            ...(isUnified &&
              sectionName === "инсоляция" && { total_day_efforts: 0 }),
          },
        };
      }
    }

    console.log(
      "[createEstimateContext] Загружено деталей секций (из construction_design_detailed):",
      Object.keys(sectionsData).length,
    );

    // Обновляем estimateData, объединяя разделы для обратной совместимости.
    // include_bonuses для рабочего проектирования: используем значение с бэкенда или true по умолчанию.
    const updatedEstimateData = {
      ...estimateData,
      construction_design: allSections,
      include_bonuses: estimateData.include_bonuses ?? true,
    };

    return {
      estimate: updatedEstimateData,
      sections: sectionsData,
      projectId,
    };
  } catch (error) {
    console.error("Error creating estimate context:", error);
    throw error;
  }
};

// Получить контекст для конкретной секции (один GET сметы, секция из construction_design_detailed)
export const getSectionContext = async (projectId, sectionId) => {
  try {
    const estimateData = await getProjectEstimate(projectId);
    const sectionsData = buildSectionDetailsFromDetailed(
      estimateData.construction_design_detailed,
    );
    const sectionData = sectionsData[sectionId] ||
      sectionsData[parseInt(sectionId, 10)] || {
        bonus_percent: 0,
        average_salary: 0,
        block_expenditures: [],
        section_id: sectionId,
      };

    const allSections = [
      ...(estimateData?.construction_design || []),
      ...(estimateData?.construction_design_project || []),
    ];
    const updatedEstimateData = {
      ...estimateData,
      construction_design: allSections,
    };

    return {
      estimate: updatedEstimateData,
      section: sectionData,
      projectId,
      sectionId,
    };
  } catch (error) {
    console.error("Error getting section context:", error);
    throw error;
  }
};

// Получить типы информации для CP TEI
export const getCpTeiInfTypes = async () => {
  try {
    const response = await fetch(
      `${getAPIURL()}project_estimates/cp_tei_inf_types`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching CP TEI inf types:", error);
    throw error;
  }
};

// Получить типы инженерных помещений для CP TEI
export const getCpTeiEngrooms = async () => {
  try {
    const response = await fetch(
      `${getAPIURL()}project_estimates/cp_tei_engrooms`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching CP TEI engrooms:", error);
    throw error;
  }
};

// Получить все секции проекта
export const getAllSections = async () => {
  try {
    const response = await fetch(`${getAPIURL()}projects/sections/all`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching all sections:", error);
    throw error;
  }
};

// Добавить новый раздел проекта
export const addProjectSection = async (name) => {
  try {
    const response = await fetch(`${getAPIURL()}projects/sections/add`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ name }),
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // ignore
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error adding project section:", error);
    throw error;
  }
};

// Удалить разделы проекта из сметы
export const deleteProjectSections = async (projectId, sectionIds) => {
  try {
    if (!sectionIds || sectionIds.length === 0) {
      return { message: "Нет разделов для удаления" };
    }

    if (!projectId) {
      throw new Error("projectId не указан");
    }

    const url = `${getAPIURL()}project_estimates/projects/${projectId}/sections/${
      sectionIds[0]
    }/delete`;
    console.log("DELETE запрос на URL:", url);
    console.log("Payload:", { section_ids: sectionIds });

    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ section_ids: sectionIds }),
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // ignore
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error deleting project sections:", error);
    throw error;
  }
};

// Получить данные CP TEI для проекта
// @param {number} projectId - ID проекта
// @param {number|null} version - Опциональный номер версии для получения конкретной версии
export const getCpTeis = async (projectId, version = null) => {
  try {
    // Добавляем timestamp для предотвращения кэширования
    const timestamp = new Date().getTime();

    // Формируем URL с опциональным параметром version
    let url = `${getAPIURL()}project_estimates/cp_teis/${projectId}?_t=${timestamp}`;
    if (version !== null && version !== undefined) {
      url += `&version=${version}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    if (!String(error.message || "").includes("404")) {
      console.error("Error fetching CP TEIs:", error);
    }
    throw error;
  }
};

// Рассчитать общую площадь CP TEI
export const calculateTotalArea = async (data) => {
  try {
    const response = await fetch(
      `${getAPIURL()}project_estimates/cp_teis/calculate_total_area`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(data),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;

      try {
        const errorData = await response.json();
        console.error("calculateTotalArea - ошибка сервера:", errorData);
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          console.error("calculateTotalArea - текст ошибки:", errorText);
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }

      throw new Error(errorMessage);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error("Error calculating total area:", error);
    throw error;
  }
};

// Добавить новый блок CP TEI
export const addCpTei = async (data) => {
  try {
    // Проверяем и нормализуем данные перед отправкой
    const normalizedData = {
      ...data,
      floor_count: parseInt(data.floor_count) || 0,
      apartments_count: parseInt(data.apartments_count) || 0,
      basement_area: parseFloat(data.basement_area) || 0,
      total_area: parseFloat(data.total_area) || 0,
      inf_type_id: parseInt(data.inf_type_id) || 0,
      project_id: parseInt(data.project_id) || 0,
      block_id: parseInt(data.block_id) || 0,
      eng_room_ids: Array.isArray(data.eng_room_ids)
        ? data.eng_room_ids.map((id) => parseInt(id))
        : [],
      typical_floors: Array.isArray(data.typical_floors)
        ? data.typical_floors.map((floor) => ({
            floor_number_from: parseInt(floor.floor_number_from) || 0,
            floor_number_to: parseInt(floor.floor_number_to) || 0,
            area: parseFloat(floor.area) || 0,
            floors_count: parseInt(floor.floors_count) || 0,
            total_area: parseFloat(floor.total_area) || 0,
          }))
        : [],
      typical_floors_area: parseFloat(data.typical_floors_area) || 0,
      typical_floors_count: parseInt(data.typical_floors_count) || 0,
      non_typical_floors: Array.isArray(data.non_typical_floors)
        ? data.non_typical_floors.map((floor) => ({
            commercial_area: parseFloat(floor.commercial_area) || 0,
            other_area: parseFloat(floor.other_area) || 0,
            floor_number: parseInt(floor.floor_number) || 0,
            total_area: parseFloat(floor.total_area) || 0,
          }))
        : [],
    };

    // Для паркинга и части блоков типовые этажи могут быть пустыми — это допустимо, не логируем предупреждение.

    const response = await fetch(
      `${getAPIURL()}project_estimates/cp_teis/add`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(normalizedData),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        // Если не удалось получить JSON, используем текст
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }

      throw new Error(errorMessage);
    }

    const responseData = await response.json();

    // Проверяем структуру ответа
    if (responseData && responseData.data && responseData.data.id) {
    } else {
      console.warn("Неожиданная структура ответа от сервера:", responseData);
    }

    return responseData;
  } catch (error) {
    console.error("Error adding CP TEI:", error);
    throw error;
  }
};

// Обновить существующий блок CP TEI
export const updateCpTei = async (cpTeiId, data) => {
  try {
    // Дополнительная валидация входных данных
    if (
      !cpTeiId ||
      cpTeiId === "local" ||
      cpTeiId === "temp" ||
      cpTeiId < 0 ||
      String(cpTeiId).length > 13
    ) {
      console.error("Обнаружен некорректный ID блока:", cpTeiId);
      console.error("Возможно, это временный ID, созданный через Date.now()");
      throw new Error(
        `Некорректный ID блока: ${cpTeiId}. Попробуйте перезагрузить страницу.`,
      );
    }

    // Проверяем и нормализуем данные перед отправкой
    const normalizedData = {
      ...data,
      floor_count: parseInt(data.floor_count) || 0,
      apartments_count: parseInt(data.apartments_count) || 0,
      basement_area: parseFloat(data.basement_area) || 0,
      total_area: parseFloat(data.total_area) || 0,
      inf_type_id: parseInt(data.inf_type_id) || 0,
      project_id: parseInt(data.project_id) || 0,
      block_id: parseInt(data.block_id) || 0,
      eng_room_ids: Array.isArray(data.eng_room_ids)
        ? data.eng_room_ids.map((id) => parseInt(id))
        : [],
      typical_floors: Array.isArray(data.typical_floors)
        ? data.typical_floors.map((floor) => ({
            floor_number_from: parseInt(floor.floor_number_from) || 0,
            floor_number_to: parseInt(floor.floor_number_to) || 0,
            area: parseFloat(floor.area) || 0,
            floors_count: parseInt(floor.floors_count) || 0,
            total_area: parseFloat(floor.total_area) || 0,
          }))
        : [],
      typical_floors_area: parseFloat(data.typical_floors_area) || 0,
      typical_floors_count: parseInt(data.typical_floors_count) || 0,
      non_typical_floors: Array.isArray(data.non_typical_floors)
        ? data.non_typical_floors.map((floor) => ({
            commercial_area: parseFloat(floor.commercial_area) || 0,
            other_area: parseFloat(floor.other_area) || 0,
            floor_number: parseInt(floor.floor_number) || 0,
            total_area: parseFloat(floor.total_area) || 0,
          }))
        : [],
    };

    // Примечание: сервер требует project_id и block_id даже при обновлении
    // Поэтому не удаляем эти поля

    const response = await fetch(
      `${getAPIURL()}project_estimates/cp_teis/${cpTeiId}/update`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(normalizedData),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      let errorData = null;

      try {
        errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        // Если не удалось получить JSON, используем текст
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }

      // Дополнительная диагностика для 500 ошибок
      if (response.status === 500) {
        console.error("=== ДИАГНОСТИКА 500 ОШИБКИ ===");
        console.error(
          "URL:",
          `${getAPIURL()}project_estimates/cp_teis/${cpTeiId}/update`,
        );
        console.error("ID блока:", cpTeiId);
        console.error(
          "Отправленные данные:",
          JSON.stringify(normalizedData, null, 2),
        );
        console.error("Заголовки запроса:", {
          "Content-Type": "application/json",
          credentials: "include",
        });
        console.error("================================");
      }

      console.error(`Ошибка обновления CP TEI (${response.status}):`);
      console.error(
        "Отправленные данные:",
        JSON.stringify(normalizedData, null, 2),
      );

      // Анализируем ошибку для предоставления более полезной информации
      if (errorData) {
        if (errorData.errors) {
          console.error("Детали ошибок валидации:", errorData.errors);
        }
        if (errorData.message) {
          console.error("Сообщение об ошибке:", errorData.message);
        }
        if (errorData.details) {
          console.error("Детали ошибки:", errorData.details);
        }
      }

      throw new Error(errorMessage);
    }

    const responseData = await response.json();

    // Проверяем структуру ответа
    if (responseData && responseData.data && responseData.data.id) {
    } else {
      console.warn(
        "Неожиданная структура ответа от сервера при обновлении:",
        responseData,
      );
    }

    return responseData;
  } catch (error) {
    console.error("Error updating CP TEI:", error);
    throw error;
  }
};

// Обновить коэффициент этажности блока (PATCH cp_teis/{block_id})
export const patchCpTeiFloorCoeff = async (blockId, value) => {
  try {
    const normalizedValue = Number.parseFloat(value);
    const response = await fetch(
      `${getAPIURL()}project_estimates/cp_teis/${blockId}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(
          Number.isFinite(normalizedValue) ? normalizedValue : 0,
        ),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error patching CP TEI floor coeff:", error);
    throw error;
  }
};

// Удалить блок CP TEI
export const deleteCpTei = async (cpTeiId) => {
  try {
    const response = await fetch(
      `${getAPIURL()}project_estimates/cp_teis/${cpTeiId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        // Если не удалось получить JSON, используем текст
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    // Для DELETE запроса может не быть тела ответа
    if (response.status === 204) {
      return { success: true };
    }

    return await response.json();
  } catch (error) {
    console.error("Error deleting CP TEI:", error);
    throw error;
  }
};

/**
 * Получить смету проекта (Get Smeta).
 * GET /project_estimates/projects/{project_id}
 * @param {number} projectId - ID проекта (path)
 * @param {number|null} [version] - Опциональный номер версии (query)
 * @returns {Promise<Object>} Ответ: editable, version, construction_design, bim_coordination, quality_control, contract_sections и др.
 */
export const getProjectEstimate = async (projectId, version = null) => {
  try {
    let url = `${getAPIURL()}project_estimates/projects/${projectId}`;
    if (version !== null && version !== undefined) {
      url += `?version=${version}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    // 404 (смета не найдена) — ожидаемая ситуация, не логируем
    if (!String(error.message || "").includes("404")) {
      console.error("Error fetching project estimate data:", error);
    }
    throw error;
  }
};

// Получить данные о строительном проектировании для проекта (один GET, детали секций из construction_design_detailed)
export const getConstructionDesign = async (projectId) => {
  try {
    const data = await getProjectEstimate(projectId);

    // Объединяем разделы из construction_design и construction_design_project
    const allSections = [
      ...(data.construction_design || []),
      ...(data.construction_design_project || []),
    ].sort(
      (a, b) =>
        (a.order_index ?? Number.MAX_SAFE_INTEGER) -
        (b.order_index ?? Number.MAX_SAFE_INTEGER),
    );

    // Детали секций из одного ответа (без GET /sections/{id})
    const section_details = buildSectionDetailsFromDetailed(
      data.construction_design_detailed,
    );

    // Преобразуем данные в формат, ожидаемый компонентами
    return {
      // Основные данные - объединенные разделы
      sections: allSections,
      // Детали по каждой секции (bonus_percent, average_salary, block_expenditures)
      section_details,

      // Новые ключи API
      contract_value: safeNumber(data.contract_value, 0),
      contract_value_before_nds: safeNumber(data.contract_value_before_nds, 0),
      contract_value_construction: safeNumber(
        data.contract_value_construction,
        0,
      ),
      contract_value_construction_percent: safeNumber(
        data.contract_value_construction_percent,
        0,
      ),
      cost_price: safeNumber(data.cost_price, 0),
      difference: safeNumber(data.difference, 0),
      total_area: safeNumber(data.total_area, 0),

      // Блоки/вкладки
      bim_coordination: data.bim_coordination || {},
      quality_control: data.quality_control || {},
      project_delivery: data.project_delivery || {},
      project_management: data.project_management || {},

      // Итоги/проценты
      nds_value: safeNumber(data.nds_value, 0),
      indirect_expenditures: safeNumber(data.indirect_expenditures, 0),
      indirect_expenditures_percent: safeNumber(
        data.indirect_expenditures_percent,
        0,
      ),
      cost_price_percent: safeNumber(data.cost_price_percent, 0),
      pure_income: safeNumber(data.pure_income, 0),
      pure_income_percent: safeNumber(data.pure_income_percent, 0),
      total_construction_design: data.total_construction_design || {},
      bonus_percent_construction_design: safeNumber(
        data.bonus_percent_construction_design,
        0,
      ),
      include_bonuses: data.include_bonuses ?? true,

      // Legacy-ключи (для обратной совместимости старых компонентов)
      consent_price: safeNumber(data.contract_value, 0),
      consent_price_before_nds: safeNumber(data.contract_value_before_nds, 0),
      consent_price_construction: safeNumber(
        data.contract_value_construction,
        0,
      ),
      cost_value_construction: safeNumber(data.contract_value_construction, 0),
    };
  } catch (error) {
    console.error("Error fetching construction design data:", error);
    throw error;
  }
};

// Получить данные секции строительного проектирования
export const getSectionConstructionDesign = async (
  projectId,
  projectSectionId,
) => {
  try {
    const response = await fetch(
      `${getAPIURL()}project_estimates/projects/${projectId}/sections/${projectSectionId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error("Error fetching section construction design data:", error);
    throw error;
  }
};

// Обновить данные строительного проектирования для конкретной секции
export const updateSectionConstructionDesign = async (
  projectId,
  projectSectionId,
  data,
) => {
  try {
    const response = await fetch(
      `${getAPIURL()}project_estimates/projects/${projectId}/sections/${projectSectionId}/construction_design`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(data),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        // Если не удалось получить JSON, используем текст
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating section construction design data:", error);
    throw error;
  }
};

// Функция для формирования payload калькуляции согласно новому API
const buildCalculationPayload = async (
  projectId,
  estimateData,
  sectionsData,
) => {
  // Получаем детали для всех секций
  const allSectionDetails = sectionsData || {};

  // Вспомогательная функция для безопасного получения числового значения
  const getNumericValue = (value, defaultValue = 0) => {
    if (typeof value === "number" && !isNaN(value)) {
      return value;
    }
    if (typeof value === "string" && !isNaN(parseFloat(value))) {
      return parseFloat(value);
    }
    return defaultValue;
  };

  // Вспомогательная функция для проверки, является ли раздел единым (без блоков)
  const isUnifiedSection = (sectionName) => {
    const name = sectionName?.toLowerCase().trim();
    return name === "инсоляция" || name === "гп";
  };

  // Порядок разделов: объединённый список, отсортированный по order_index
  const orderedSections = [
    ...(estimateData.construction_design || []),
    ...(estimateData.construction_design_project || []),
  ].sort(
    (a, b) =>
      (a.order_index ?? Number.MAX_SAFE_INTEGER) -
      (b.order_index ?? Number.MAX_SAFE_INTEGER),
  );

  const regularSections = [];
  const unifiedSections = [];
  // В payload для единых разделов (Инсоляция, ГП) — не более одного вхождения на section_id,
  // иначе при повторном blur без изменений получаем разный payload и разные итоги.
  const unifiedSectionIdsSeen = new Set();

  // Бэкенд при save требует threshold_percent > 0 и average_salary > 0
  const ensurePositive = (val, fallback) =>
    val != null && Number(val) > 0 ? Number(val) : fallback;

  orderedSections.forEach((sectionFromEstimate, order_index) => {
    const sectionId = sectionFromEstimate?.section?.id;
    if (sectionId == null) return;

    const sectionDetails = allSectionDetails[sectionId];
    const sectionName =
      sectionFromEstimate?.section?.name || sectionDetails?.section_name;

    if (isUnifiedSection(sectionName)) {
      if (unifiedSectionIdsSeen.has(sectionId)) return;
      unifiedSectionIdsSeen.add(sectionId);
      const isInsolation = sectionName?.toLowerCase().trim() === "инсоляция";
      const rawThreshold =
        sectionDetails?.threshold_percent ||
        sectionFromEstimate?.expenditures_percent?.threshold;
      const rawSalary = sectionDetails?.average_salary;
      unifiedSections.push({
        section_id: sectionId,
        threshold_percent: ensurePositive(rawThreshold, 0.01),
        average_salary: ensurePositive(rawSalary, 1),
        day_efforts: isInsolation ? sectionDetails?.total_day_efforts || 0 : 0,
        order_index: unifiedSections.length,
      });
    } else {
      const blocks = (sectionDetails?.block_expenditures || []).map(
        (block) => ({
          tei_id: block.block?.tei_id || 0,
          day_efforts: block.day_efforts || 0,
        }),
      );
      const rawThreshold =
        sectionDetails?.threshold_percent ||
        sectionFromEstimate?.expenditures_percent?.threshold;
      const rawSalary = sectionDetails?.average_salary;
      const result = {
        section_id: sectionId,
        threshold_percent: ensurePositive(rawThreshold, 0.01),
        average_salary: ensurePositive(rawSalary, 1),
        blocks,
        order_index,
      };
      if (
        (rawThreshold == null || Number(rawThreshold) <= 0) &&
        (rawSalary == null || Number(rawSalary) <= 0) &&
        blocks.length === 0
      ) {
        console.warn(
          `[buildCalculationPayload] ⚠️ Раздел ${sectionId} имеет нулевые значения и может не сохраниться`,
        );
      }
      regularSections.push(result);
    }
  });

  const constructionDesign = regularSections;
  const constructionDesignProject = unifiedSections;

  // Приоритет у согласованного значения из секций (оно обновляется после калькуляции/редактирования),
  // иначе — estimateData (при загрузке контекст может ещё не иметь актуального bonus_percent_construction_design)
  const bonusValues = Object.values(allSectionDetails || {})
    .map((det) => det.bonus_percent)
    .filter((val) => val !== undefined && val !== null);

  // Берём первое ненулевое значение из секций, если все значения одинаковые
  // Если значения разные - используем первое ненулевое или fallback на estimateData
  const allSameBonus =
    bonusValues.length > 0 && bonusValues.every((v) => v === bonusValues[0]);
  const firstNonZeroBonus = bonusValues.find((v) => v > 0);
  const fromSections = allSameBonus
    ? bonusValues[0]
    : firstNonZeroBonus !== undefined
      ? firstNonZeroBonus
      : undefined;

  // Определяем глобальный процент бонусов:
  // 1. Из секций (если есть согласованное значение)
  // 2. Из estimateData.bonus_percent_construction_design (fallback)
  // 3. 0 по умолчанию (если ничего не найдено)
  const globalBonusPercent =
    fromSections !== undefined
      ? Number(fromSections)
      : estimateData.bonus_percent_construction_design !== undefined &&
          estimateData.bonus_percent_construction_design !== null
        ? Number(estimateData.bonus_percent_construction_design)
        : getNumericValue(estimateData.bonus_percent_construction_design, 0);

  // section_id: приоритет у role.section_id (GET/ответ API теперь везде отдаёт section_id), иначе — по имени раздела
  const sectionsByName = new Map();
  const addSections = (arr) => {
    (arr || []).forEach((item) => {
      const id = item?.section?.id;
      const name = item?.section?.name?.trim();
      if (id != null && name != null) sectionsByName.set(name, id);
    });
  };
  addSections(estimateData.construction_design);
  addSections(estimateData.construction_design_project);
  const bimSectionId = (role) => {
    if (role.section_id != null) return role.section_id;
    const name = role.role?.trim();
    if (!name?.startsWith("BIM ")) return null;
    return sectionsByName.get(name.replace(/^BIM\s+/, "").trim()) ?? null;
  };
  const qualitySectionId = (role) => {
    if (role.section_id != null) return role.section_id;
    const n = role.role?.trim();
    if (!n || n === "РО 5" || n === "Норм Контроль" || n === "Норм.контроль")
      return null;
    return sectionsByName.get(n) ?? null;
  };
  const deliverySectionId = (role) =>
    role.section_id != null
      ? role.section_id
      : (sectionsByName.get(role.role?.trim()) ?? null);

  // Формируем блоки для калькуляции
  const payload = {
    include_bonuses: estimateData.include_bonuses ?? true,
    bonus_percent_construction_design: globalBonusPercent,
    construction_design: constructionDesign,
    construction_design_project: constructionDesignProject, // Всегда включаем, даже если массив пустой (бэкенд требует это поле)
    bim_coordination: {
      include_bonuses: estimateData.bim_coordination?.include_bonuses ?? true,
      quality_control_percent:
        estimateData.bim_coordination?.quality_control_percent || 0,
      direct_bonus_percent:
        estimateData.bim_coordination?.direct_bonus_percent || 0,
      bim_role_salaries: (
        estimateData.bim_coordination?.bim_role_salaries ||
        estimateData.bim_coordination?.roles ||
        []
      ).map((role, index) => ({
        role: role.role,
        average_salary: role.average_salary || 0,
        section_id: bimSectionId(role),
        order_index: index,
      })),
    },
    quality_control: {
      include_bonuses: estimateData.quality_control?.include_bonuses ?? true,
      quality_control_percent:
        estimateData.quality_control?.quality_control_percent || 0,
      direct_bonus_percent:
        estimateData.quality_control?.direct_bonus_percent || 0,
      normative_control_efforts:
        estimateData.quality_control?.normative_control_efforts || 0,
      vk_control_efforts: estimateData.quality_control?.vk_control_efforts || 0,
      quality_control_role_salaries: (
        estimateData.quality_control?.quality_control_role_salaries ||
        estimateData.quality_control?.roles ||
        []
      ).map((role, index) => ({
        role: role.role,
        average_salary: role.average_salary || 0,
        section_id: qualitySectionId(role),
        order_index: index,
      })),
    },
    project_delivery: {
      include_bonuses: estimateData.project_delivery?.include_bonuses ?? true,
      average_salary: estimateData.project_delivery?.average_salary || 0,
      quality_control_percent:
        estimateData.project_delivery?.quality_control_percent || 0,
      direct_bonus_percent:
        estimateData.project_delivery?.direct_bonus_percent || 0,
      project_delivery_role_salaries: (
        estimateData.project_delivery?.project_delivery_role_salaries ||
        estimateData.project_delivery?.roles ||
        []
      ).map((role, index) => ({
        role: role.role,
        average_salary: role.average_salary || 0,
        section_id: deliverySectionId(role),
        order_index: index,
      })),
    },
    project_management: {
      include_bonuses: estimateData.project_management?.include_bonuses ?? true,
      quality_control_percent:
        estimateData.project_management?.quality_control_percent || 0,
      direct_bonus_percent:
        estimateData.project_management?.direct_bonus_percent || 0,
      administration_efforts:
        estimateData.project_management?.administration_efforts || 0,
      management_role_salaries: (
        estimateData.project_management?.roles || []
      ).map((role, index) => ({
        role: role.role,
        average_salary: role.average_salary || 0,
        coefficient: role.coefficient || 0,
        order_index: index,
      })),
    },
    contract_sections: (() => {
      // Поддержка новой структуры: contract_sections.sections или старого массива
      let sectionsArray = [];
      if (estimateData.contract_sections) {
        if (
          estimateData.contract_sections.sections &&
          Array.isArray(estimateData.contract_sections.sections)
        ) {
          // Новая структура: объект с полем sections
          sectionsArray = estimateData.contract_sections.sections;
        } else if (Array.isArray(estimateData.contract_sections)) {
          // Старая структура: массив
          sectionsArray = estimateData.contract_sections;
        }
      }

      return sectionsArray.map((cs) => ({
        section_id: cs.section?.id || cs.section_id,
        amount: cs.amount || 0,
      }));
    })(),
  };

  return payload;
};

// Функция для обновления контекста с результатами калькуляции
export const updateContextWithCalculationResults = (
  context,
  calculationResults,
) => {
  if (!calculationResults) return context;

  console.log("[updateContextWithCalculationResults] Входные данные:", {
    context_sections_keys: Object.keys(context.sections || {}),
    context_sections_27: context.sections?.[27],
    calculationResults_construction_design_detailed:
      calculationResults.construction_design_detailed?.find(
        (d) => d.section_id === 27,
      ),
  });

  // Обновляем данные сметы результатами калькуляции
  const updatedEstimate = {
    ...context.estimate,
    ...calculationResults,
  };

  // Обновляем construction_design с новыми данными из construction_design_detailed
  if (calculationResults.construction_design_detailed) {
    const updatedConstructionDesign =
      updatedEstimate.construction_design?.map((section) => {
        const detailedSection =
          calculationResults.construction_design_detailed.find(
            (det) => det.section_id === section.section.id,
          );

        if (detailedSection) {
          // Обновляем day_efforts на основе данных из block_expenditures
          const totalDirectEfforts =
            detailedSection.block_expenditures?.reduce(
              (sum, block) => sum + (block.day_efforts || 0),
              0,
            ) || 0;
          const totalBonusEfforts =
            detailedSection.block_expenditures?.reduce(
              (sum, block) => sum + (block.bonus_day_efforts || 0),
              0,
            ) || 0;

          return {
            ...section,
            day_efforts: {
              direct: totalDirectEfforts,
              bonus: totalBonusEfforts,
              total: totalDirectEfforts + totalBonusEfforts,
            },
          };
        }

        return section;
      }) || [];

    updatedEstimate.construction_design = updatedConstructionDesign;
  }

  // Обновляем construction_design_project с новыми данными из результатов калькуляции
  if (calculationResults.construction_design_project) {
    updatedEstimate.construction_design_project =
      calculationResults.construction_design_project;
  }

  // Объединяем разделы для обратной совместимости, избегая дубликатов
  const existingSectionIds = new Set(
    (updatedEstimate.construction_design || [])
      .map((s) => s.section?.id)
      .filter(Boolean),
  );

  const projectSections = (
    updatedEstimate.construction_design_project || []
  ).filter((s) => {
    const sectionId = s.section?.id;
    return sectionId && !existingSectionIds.has(sectionId);
  });

  const allSections = [
    ...(updatedEstimate.construction_design || []),
    ...projectSections,
  ];
  updatedEstimate.construction_design = allSections;

  // Сохраняем contract_sections из исходных данных, если в результатах калькуляции они пустые или отсутствуют
  // contract_sections используются только как входные данные для калькуляции и не должны изменяться результатами
  if (
    !calculationResults.contract_sections ||
    (Array.isArray(calculationResults.contract_sections) &&
      calculationResults.contract_sections.length === 0) ||
    (calculationResults.contract_sections.sections &&
      calculationResults.contract_sections.sections.length === 0)
  ) {
    // Сохраняем исходные contract_sections из context.estimate
    if (context.estimate?.contract_sections) {
      // Проверяем, является ли это массивом или объектом с sections
      const hasSections =
        Array.isArray(context.estimate.contract_sections) &&
        context.estimate.contract_sections.length > 0;
      const hasSectionsObject =
        context.estimate.contract_sections.sections &&
        Array.isArray(context.estimate.contract_sections.sections) &&
        context.estimate.contract_sections.sections.length > 0;

      if (hasSections || hasSectionsObject) {
        updatedEstimate.contract_sections = context.estimate.contract_sections;
      }
    }
  }

  // Сохраняем введённые пользователем administration_efforts и normative_control_efforts
  // из контекста, чтобы они не обнулялись при калькуляции с других вкладок (например, разделы по договору).
  if (
    context.estimate?.project_management &&
    context.estimate.project_management.administration_efforts !== undefined &&
    context.estimate.project_management.administration_efforts !== null
  ) {
    updatedEstimate.project_management = {
      ...(updatedEstimate.project_management || {}),
      administration_efforts:
        context.estimate.project_management.administration_efforts,
    };
  }
  if (
    context.estimate?.quality_control &&
    context.estimate.quality_control.normative_control_efforts !== undefined &&
    context.estimate.quality_control.normative_control_efforts !== null
  ) {
    updatedEstimate.quality_control = {
      ...(updatedEstimate.quality_control || {}),
      normative_control_efforts:
        context.estimate.quality_control.normative_control_efforts,
    };
  }

  // Обновляем детали секций с новыми данными из construction_design_detailed
  const updatedSections = { ...context.sections };

  if (calculationResults.construction_design_detailed) {
    calculationResults.construction_design_detailed.forEach((sectionDetail) => {
      const sectionId = sectionDetail.section_id;
      const existingSection = updatedSections[sectionId];

      // Сохраняем average_salary из текущего контекста, если в ответе калькуляции оно 0 или отсутствует
      // Это важно для единых разделов (Инсоляция, ГП), где average_salary вводится пользователем
      const preservedAverageSalary =
        sectionDetail.average_salary || existingSection?.average_salary || 0;
      // Аналогично сохраняем section_name
      const preservedSectionName = existingSection?.section_name;
      // Сохраняем bonus_percent из текущего контекста, если API не вернул его
      // API может не возвращать bonus_percent в construction_design_detailed
      const preservedBonusPercent =
        sectionDetail.bonus_percent !== undefined &&
        sectionDetail.bonus_percent !== null
          ? sectionDetail.bonus_percent
          : (existingSection?.bonus_percent ?? 0);

      const mappedBlocks =
        sectionDetail.block_expenditures?.map((block) => ({
          block: {
            id: block.block?.id || block.block?.tei_id,
            tei_id: block.block?.tei_id,
            name: block.block?.name || `Block ${block.block?.tei_id}`,
          },
          floor_count: block.floor_count || 0,
          day_efforts: block.day_efforts || 0,
          bonus_day_efforts: block.bonus_day_efforts || 0,
          money_expenditures: block.money_expenditures || 0,
          bonus_money_expenditures: block.bonus_money_expenditures || 0,
        })) || [];
      const sortedBlocks = sortBlockExpendituresOrder(mappedBlocks);

      if (existingSection) {
        // Обновляем существующие данные секции (блоки: Паркинг → Блок 1,2,… → остальные)
        updatedSections[sectionId] = {
          ...existingSection,
          bonus_percent: preservedBonusPercent,
          average_salary: preservedAverageSalary,
          section_name: preservedSectionName,
          block_expenditures: sortedBlocks,
        };
      } else {
        // Создаем новые данные секции, если их не было
        updatedSections[sectionId] = {
          bonus_percent: preservedBonusPercent,
          average_salary: preservedAverageSalary,
          section_name: preservedSectionName,
          block_expenditures: sortedBlocks,
        };
      }
    });
  }

  // Обновляем детали секций для единых разделов из construction_design_project
  // Для единых разделов данные уже должны быть загружены через getSectionConstructionDesign
  // Здесь мы просто обновляем total_day_efforts для "Инсоляции", если нужно
  if (calculationResults.construction_design_project) {
    calculationResults.construction_design_project.forEach((section) => {
      const sectionId = section.section?.id;
      if (!sectionId) return;

      // Находим название раздела для определения типа
      const sectionName = section.section?.name?.toLowerCase().trim();
      const isInsolation = sectionName === "инсоляция";

      if (updatedSections[sectionId] && isInsolation) {
        // Обновляем total_day_efforts для "Инсоляции" из результатов калькуляции
        updatedSections[sectionId] = {
          ...updatedSections[sectionId],
          total_day_efforts:
            section.day_efforts?.direct ||
            section.day_efforts?.total ||
            updatedSections[sectionId].total_day_efforts ||
            0,
        };
      }
      // Для "ГП" и других единых разделов данные остаются как есть
    });
  }

  console.log("[updateContextWithCalculationResults] Результат:", {
    updatedSections_27: updatedSections[27],
  });

  const result = {
    ...context,
    estimate: updatedEstimate,
    sections: updatedSections,
  };

  return result;
};

// Функция для сохранения с обновлением контекста
export const saveAndUpdateContext = async (
  projectId,
  estimateData,
  sectionsData,
) => {
  try {
    // Сохраняем данные
    const saveResult = await saveConstructionDesignPreservingData(
      projectId,
      estimateData,
      sectionsData,
    );

    // Получаем обновленные данные после сохранения
    const updatedContext = await createEstimateContext(projectId);

    // СТАБИЛЬНЫЙ ПОРЯДОК РАЗДЕЛОВ:
    // После сохранения бэкенд может вернуть construction_design в другом порядке.
    // Чтобы список разделов в WorkTab не «перепрыгивал», приводим порядок
    // к тому, который был до сохранения (по ID секций).
    try {
      const beforeSections = estimateData?.construction_design || [];
      const afterSections = updatedContext.estimate?.construction_design || [];

      if (beforeSections.length && afterSections.length) {
        const orderMap = {};
        beforeSections.forEach((section, index) => {
          const id = section?.section?.id;
          if (id !== undefined && id !== null && orderMap[id] === undefined) {
            orderMap[id] = index;
          }
        });

        const sortedAfter = [...afterSections].sort((a, b) => {
          const idA = a?.section?.id;
          const idB = b?.section?.id;
          const orderA =
            idA !== undefined && idA !== null && orderMap[idA] !== undefined
              ? orderMap[idA]
              : Number.MAX_SAFE_INTEGER;
          const orderB =
            idB !== undefined && idB !== null && orderMap[idB] !== undefined
              ? orderMap[idB]
              : Number.MAX_SAFE_INTEGER;
          return orderA - orderB;
        });

        updatedContext.estimate = {
          ...updatedContext.estimate,
          construction_design: sortedAfter,
        };
      }
    } catch (orderError) {
      console.warn(
        "[saveAndUpdateContext] Не удалось стабилизировать порядок разделов:",
        orderError,
      );
    }

    // Сравниваем разделы до и после сохранения
    const sectionIdsAfter = new Set(
      updatedContext.estimate?.construction_design
        ?.map((s) => s.section?.id)
        .filter(Boolean) || [],
    );
    const sectionIdsBefore = new Set(
      estimateData?.construction_design
        ?.map((s) => s.section?.id)
        .filter(Boolean) || [],
    );
    const missingSections = Array.from(sectionIdsBefore).filter(
      (id) => !sectionIdsAfter.has(id),
    );
    if (missingSections.length > 0) {
      console.warn(
        "[saveAndUpdateContext] ⚠️ Потеряны разделы после сохранения:",
        missingSections,
      );
      console.warn(
        "[saveAndUpdateContext] Было разделов:",
        sectionIdsBefore.size,
        "Стало:",
        sectionIdsAfter.size,
      );
      console.warn(
        "[saveAndUpdateContext] ID разделов до сохранения:",
        Array.from(sectionIdsBefore).sort((a, b) => a - b),
      );
      console.warn(
        "[saveAndUpdateContext] ID разделов после сохранения:",
        Array.from(sectionIdsAfter).sort((a, b) => a - b),
      );
    } else {
      console.log(
        "[saveAndUpdateContext] ✅ Все разделы сохранены успешно. Было:",
        sectionIdsBefore.size,
        "Стало:",
        sectionIdsAfter.size,
      );
    }

    return {
      saveResult,
      context: updatedContext,
    };
  } catch (error) {
    console.error("Error saving and updating context:", error);
    throw error;
  }
};

// Рассчитать строительное проектирование (обновленная версия)
export const calculateConstructionDesign = async (
  projectId,
  estimateData,
  sectionsData,
) => {
  try {
    // Если данные не переданы, получаем их из API
    if (!estimateData) {
      const context = await createEstimateContext(projectId);
      estimateData = context.estimate;
      sectionsData = context.sections;
    }

    // Формируем payload согласно новому API
    const payload = await buildCalculationPayload(
      projectId,
      estimateData,
      sectionsData,
    );

    const response = await fetch(
      `${getAPIURL()}project_estimates/projects/${projectId}/calculate`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();

    // Возвращаем полный результат для лайв обновления интерфейса
    return result;
  } catch (error) {
    console.error("Error calculating construction design:", error);
    throw error;
  }
};

// Рассчитать строительное проектирование для конкретной секции (обновленная версия)
export const calculateSectionConstructionDesign = async (
  contextData,
  projectId,
  sectionId,
  sectionData,
  payloadData = null,
) => {
  try {
    // Используем переданный контекст или получаем через API
    const context = contextData || (await createEstimateContext(projectId));
    const estimateData = context.estimate;
    const sectionsData = context.sections;

    // Находим секцию в массивах sections
    // Сначала ищем в construction_design (обычные разделы)
    let section = estimateData.construction_design?.find(
      (section) => section.section.id === sectionId,
    );

    // Если не нашли, ищем в construction_design_project (единые разделы: ГП, Инсоляция)
    if (!section) {
      section = estimateData.construction_design_project?.find(
        (section) => section.section.id === sectionId,
      );
    }

    if (!section) {
      throw new Error(`Секция с ID ${sectionId} не найдена`);
    }

    // Обновляем данные секции в контексте, если переданы новые данные
    if (sectionData) {
      sectionsData[sectionId] = {
        ...sectionsData[sectionId],
        ...sectionData,
      };
      console.log(
        `[calculateSectionConstructionDesign] Обновлены sectionsData для секции ${sectionId}:`,
        {
          sectionId,
          sectionData,
          updated_sectionsData: sectionsData[sectionId],
        },
      );
    }

    // НЕ обновляем estimateData через payloadData, так как payloadData содержит payload для API, а не данные для обновления
    // Это было причиной обнуления значений contract_value, cost_price и т.д.

    console.log(
      `[calculateSectionConstructionDesign] Вызываем buildCalculationPayload для секции ${sectionId}:`,
      {
        sectionId,
        sectionsData_sectionId: sectionsData[sectionId],
        estimateData_construction_design_project:
          estimateData.construction_design_project?.find(
            (s) => s.section?.id === sectionId,
          ),
      },
    );

    // Используем новую функцию для формирования payload
    const payload = await buildCalculationPayload(
      projectId,
      estimateData,
      sectionsData,
    );

    const response = await fetch(
      `${getAPIURL()}project_estimates/projects/${projectId}/calculate`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        console.error("Ошибка API калькуляции секции:", errorData);
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          console.error("Ошибка API калькуляции секции (текст):", errorText);
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();

    // Проверяем, пересчитал ли бэкенд money_expenditures (для единых разделов ГП, Инсоляция)
    const gpSection = result?.construction_design_project?.find(
      (s) => s.section?.id === sectionId,
    );
    if (gpSection) {
      const payloadSection = payload.construction_design_project?.find(
        (s) => s.section_id === sectionId,
      );
      const salary = payloadSection?.average_salary || 0;
      const direct =
        gpSection.day_efforts?.direct ?? payloadSection?.day_efforts ?? 0;
      const bonus = gpSection.day_efforts?.bonus ?? 0;
      const expectedDirect =
        (typeof direct === "number" ? direct : (direct?.direct ?? 0)) * salary;
      const expectedBonus =
        (typeof bonus === "number" ? bonus : (bonus?.bonus ?? 0)) * salary;

      if (
        gpSection.money_expenditures?.direct === 0 &&
        gpSection.money_expenditures?.bonus === 0 &&
        expectedDirect > 0
      ) {
        console.warn(
          `[calculateSectionConstructionDesign] Бэкенд не пересчитал money_expenditures для секции ${sectionId}`,
        );
      }
    }

    // Обновляем контекст с результатами калькуляции
    const updatedContext = updateContextWithCalculationResults(
      contextData,
      result,
    );

    return {
      ...result,
      updatedContext: updatedContext,
    };
  } catch (error) {
    console.error("Error calculating section construction design:", error);
    throw error;
  }
};

// Сохранить данные строительного проектирования с сохранением существующих значений
// Сохранить данные строительного проектирования (обновленная версия)
export const saveConstructionDesignPreservingData = async (
  projectId,
  estimateData,
  sectionsData,
) => {
  try {
    // Если данные не переданы, получаем их из API
    if (!estimateData) {
      const context = await createEstimateContext(projectId);
      estimateData = context.estimate;
      sectionsData = context.sections;
    }

    // Используем ту же функцию для формирования payload, что и для калькуляции
    const payload = await buildCalculationPayload(
      projectId,
      estimateData,
      sectionsData,
    );

    // Логируем для отладки сохранения
    console.log(
      "[saveConstructionDesignPreservingData] estimateData.construction_design:",
      estimateData?.construction_design?.map((s) => ({
        id: s.section?.id,
        name: s.section?.name,
      })),
    );
    console.log(
      "[saveConstructionDesignPreservingData] sectionsData keys:",
      Object.keys(sectionsData || {}),
    );
    console.log(
      "[saveConstructionDesignPreservingData] payload.construction_design:",
      payload?.construction_design?.map((s) => ({
        section_id: s.section_id,
        blocks_count: s.blocks?.length || 0,
        threshold_percent: s.threshold_percent,
        average_salary: s.average_salary,
      })),
    );

    // Используем данные construction_design напрямую из payload
    // Блоки уже правильно сгруппированы по секциям в массиве blocks
    const constructionDesign = payload.construction_design || [];

    // Логируем данные перед отправкой
    console.log(
      "[saveConstructionDesignPreservingData] 📤 Полный payload.construction_design перед отправкой:",
      JSON.stringify(
        constructionDesign.map((s) => ({
          section_id: s.section_id,
          threshold_percent: s.threshold_percent,
          average_salary: s.average_salary,
          blocks_count: s.blocks?.length || 0,
          blocks: s.blocks,
        })),
        null,
        2,
      ),
    );

    // Формируем финальный payload с правильной структурой construction_design
    // где для каждой секции все блоки находятся в одном объекте
    const finalPayload = {
      ...payload,
      construction_design: constructionDesign,
    };

    const response = await fetch(
      `${getAPIURL()}project_estimates/projects/${projectId}/save`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(finalPayload),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        console.error("Ошибка API сохранения:", errorData);
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          console.error("Ошибка API сохранения (текст):", errorText);
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log(
      "[saveConstructionDesignPreservingData] Результат сохранения:",
      result,
    );
    return result;
  } catch (error) {
    console.error("Error saving construction design data:", error);
    throw error;
  }
};

// Сохранить данные строительного проектирования
export const saveConstructionDesign = async (projectId, data) => {
  try {
    const response = await fetch(
      `${getAPIURL()}project_estimates/projects/${projectId}/save`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(data),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        // Если не удалось получить JSON, используем текст
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error("Error saving construction design data:", error);
    throw error;
  }
};

// Обновить процент "На разработку" (threshold cost percent)
export const updateThresholdCostPercent = async (projectId, percent) => {
  try {
    const normalizedPercent =
      typeof percent === "number" ? percent : Number(percent);

    const response = await fetch(
      `${getAPIURL()}project_estimates/${projectId}/threshold-cost-percent`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(
          Number.isFinite(normalizedPercent) ? normalizedPercent : 0,
        ),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating threshold cost percent:", error);
    throw error;
  }
};

// Сохранить данные конкретной секции строительного проектирования
export const saveSectionConstructionDesign = async (
  projectId,
  sectionId,
  data,
) => {
  try {
    // Подготавливаем данные в формате, ожидаемом новым API
    const saveData = {
      bonus_percent: data.bonus_percent || 0,
      average_salary: data.average_salary || 0,
      block_expenditures:
        data.block_expenditures?.map((block) => ({
          block: {
            id: block.block?.id || 0,
            name: block.block?.name || "",
            tei_id: block.block?.tei_id || 0,
          },
          floor_count: block.floor_count || 0,
          day_efforts: block.day_efforts || 0,
          bonus_day_efforts: block.bonus_day_efforts || 0,
          money_expenditures: block.money_expenditures || 0,
          bonus_money_expenditures: block.bonus_money_expenditures || 0,
        })) || [],
    };

    const response = await fetch(
      `${getAPIURL()}project_estimates/projects/${projectId}/sections/${sectionId}/construction_design`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(saveData),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        // Если не удалось получить JSON, используем текст
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error saving section construction design data:", error);
    throw error;
  }
};

// Получить все компании
export const getAllCompanies = async () => {
  try {
    const response = await fetch(`${getAPIURL()}companies/all`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching companies:", error);
    throw error;
  }
};

// Получить коммерческое предложение для проекта
// @param {number} projectId - ID проекта
// @param {number|null} version - Опциональный номер версии для получения конкретной версии
export const getCommercialProposal = async (projectId, version = null) => {
  try {
    // Добавляем timestamp для предотвращения кэширования
    const timestamp = new Date().getTime();

    // Формируем URL с опциональным параметром version
    let url = `${getAPIURL()}project_estimates/projects/${projectId}/commercial_proposal?_t=${timestamp}`;
    if (version !== null && version !== undefined) {
      url += `&version=${version}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
      },
      credentials: "include",
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        // Если не удалось получить JSON, используем текст
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    if (!String(error.message || "").includes("404")) {
      console.error("Error fetching commercial proposal:", error);
    }
    throw error;
  }
};

// Загрузить изображение схемы блокировки для проекта
export const uploadProjectImage = async (projectId, imageFile) => {
  try {
    const formData = new FormData();
    formData.append("image", imageFile);

    const response = await fetch(
      `${getAPIURL()}project_estimates/cp_teis/${projectId}/upload_image`,
      {
        method: "POST",
        headers: {
          // Не устанавливаем Content-Type, так как FormData автоматически установит правильный заголовок с boundary
        },
        credentials: "include",
        body: formData,
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        // Если не удалось получить JSON, используем текст
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error("Error uploading project image:", error);
    throw error;
  }
};

// Сохранить заголовок коммерческого предложения
export const saveCpTitle = async (projectId, data) => {
  try {
    const url = `${getAPIURL()}project_estimates/projects/${projectId}/cp_title/save`;
    const requestBody = JSON.stringify(data);

    console.log("Saving CP title:", {
      url,
      projectId,
      data,
      requestBody,
      dataLength: requestBody.length,
    });

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: requestBody,
    });

    console.log("Response status:", response.status, response.statusText);

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      let errorDetails = null;

      // Пытаемся получить заголовки ответа
      const contentType = response.headers.get("content-type");
      console.log("Response Content-Type:", contentType);

      // Читаем тело ответа как текст (можно прочитать только один раз)
      try {
        const responseText = await response.text();
        errorDetails = responseText;

        // Если Content-Type указывает на JSON, пытаемся распарсить
        if (contentType && contentType.includes("application/json")) {
          try {
            const errorData = JSON.parse(responseText);
            errorMessage += ` - ${JSON.stringify(errorData)}`;
            console.error("Error response JSON:", errorData);
          } catch (parseError) {
            // Если не удалось распарсить, используем текст
            errorMessage += ` - ${responseText}`;
            console.error(
              "Error response text (failed to parse as JSON):",
              responseText,
            );
          }
        } else {
          errorMessage += ` - ${responseText}`;
          console.error("Error response text:", responseText);
        }
      } catch (e) {
        console.error("Could not read error response body:", e);
      }

      console.error("Error response details:", {
        status: response.status,
        statusText: response.statusText,
        contentType,
        details: errorDetails,
        url,
      });

      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log("Successfully saved CP title:", result);
    return result;
  } catch (error) {
    console.error("Error saving CP title:", error);
    throw error;
  }
};

// Сохранить график платежей
export const saveCpPaymentSchedule = async (projectId, paymentSchedule) => {
  try {
    const response = await fetch(
      `${getAPIURL()}project_estimates/projects/${projectId}/cp_payment_schedule/save`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(paymentSchedule),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        // Если не удалось получить JSON, используем текст
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error saving CP payment schedule:", error);
    throw error;
  }
};

// Сохранить настройки проекта (блоки, площадь участка, коэффициенты, схема)
export const saveProjectBlocks = async (projectId, data) => {
  try {
    const response = await fetch(
      `${getAPIURL()}project_estimates/cp_teis/${projectId}/blocks/save`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(data),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        // Если не удалось получить JSON, используем текст
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error saving project blocks:", error);
    throw error;
  }
};

/**
 * Отправить коммерческое предложение на согласование
 * @param {number} projectId - ID проекта
 * @param {Array<number>} approverIds - Массив ID согласующих
 * @param {number} totalPrice - Общая стоимость проекта
 * @returns {Promise<Object>} Результат отправки
 */
export const requestCommercialProposalApproval = async (
  projectId,
  approverIds,
  totalPrice,
) => {
  try {
    const response = await fetch(
      `${getAPIURL()}project_estimates/projects/${projectId}/commercial_proposal/request_for_approval`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          approver_ids: approverIds,
          total_price: totalPrice,
        }),
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Ответ сервера: ${response.status} ${response.statusText}`);
      console.error(`Тело ответа: ${errorText}`);
      throw new Error(`Ошибка при отправке на согласование: ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при отправке на согласование:", error);
    throw error;
  }
};

/**
 * Получить историю версий согласований коммерческого предложения
 * @param {number} projectId - ID проекта
 * @returns {Promise<Array>} Массив версий согласований
 */
export const getCommercialProposalApprovals = async (projectId) => {
  try {
    // Добавляем timestamp для предотвращения кэширования
    const timestamp = new Date().getTime();
    const response = await fetch(
      `${getAPIURL()}project_estimates/projects/${projectId}/commercial_proposal_approvals?_t=${timestamp}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при получении истории согласований:", error);
    throw error;
  }
};

/**
 * Отменить отправку коммерческого предложения на согласование
 * @param {number} projectId - ID проекта
 * @returns {Promise<Object>} Результат отмены согласования
 */
export const cancelCommercialProposalApproval = async (projectId) => {
  try {
    const response = await fetch(
      `${getAPIURL()}project_estimates/projects/${projectId}/commercial_proposal/cancel_approval_request`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Ответ сервера: ${response.status} ${response.statusText}`);
      console.error(`Тело ответа: ${errorText}`);
      throw new Error(
        `Ошибка при отмене согласования: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при отмене согласования:", error);
    throw error;
  }
};

/**
 * Отправить смету на согласование
 * @param {number} projectId - ID проекта
 * @param {Array<number>} approverIds - Массив ID согласующих
 * @param {number} totalPrice - Общая стоимость проекта
 * @returns {Promise<Object>} Результат отправки
 */
export const requestProjectEstimateApproval = async (
  projectId,
  approverIds,
  totalPrice,
) => {
  try {
    const response = await fetch(
      `${getAPIURL()}project_estimates/projects/${projectId}/request_for_approval`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          approver_ids: approverIds,
          total_price: totalPrice,
        }),
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Ответ сервера: ${response.status} ${response.statusText}`);
      console.error(`Тело ответа: ${errorText}`);
      throw new Error(`Ошибка при отправке на согласование: ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при отправке на согласование:", error);
    throw error;
  }
};

/**
 * Получить историю согласований сметы
 * @param {number} projectId - ID проекта
 * @returns {Promise<Array>} Массив версий согласований
 */
export const getProjectEstimateApprovals = async (projectId) => {
  try {
    const response = await fetch(
      `${getAPIURL()}project_estimates/projects/${projectId}/approvals`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при получении истории согласований:", error);
    throw error;
  }
};

/**
 * Восстановить версию ТЭП, Сметы и КП
 * @param {number} projectId - ID проекта
 * @param {number|null} version - Версия для восстановления (null для создания новой копии текущей версии)
 * @returns {Promise<Object>} Результат восстановления версии с полями message и version
 */
export const restoreVersion = async (projectId, version = null) => {
  try {
    // Формируем body: если version === null, не отправляем поле version
    const body = version !== null ? { version: version } : {};

    const response = await fetch(
      `${getAPIURL()}project_estimates/project/${projectId}/restore_version`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(body),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при восстановлении версии:", error);
    throw error;
  }
};

/**
 * Получить коммерческое предложение для проекта эскизного проектирования
 * @param {number} projectId - ID проекта
 * @returns {Promise<Object>} Данные коммерческого предложения
 */
export const getSketchDesignCommercialOffer = async (projectId) => {
  try {
    const timestamp = new Date().getTime();
    const response = await fetch(
      `${getAPIURL()}sketch_design/commercial_offer/calculate/${projectId}?_t=${timestamp}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching sketch design commercial offer:", error);
    throw error;
  }
};

/**
 * Рассчитать смету проекта эскизного проектирования
 * GET sketch_design/estimate/calculate/{project_id}
 * @param {number} projectId - ID проекта
 * @returns {Promise<Object>} Данные сметы
 */
export const getSketchDesignEstimate = async (projectId) => {
  try {
    const timestamp = new Date().getTime();
    const response = await fetch(
      `${getAPIURL()}sketch_design/estimate/calculate/${projectId}?_t=${timestamp}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {}
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching sketch design estimate:", error);
    throw error;
  }
};

/**
 * Обновить смету проекта эскизного проектирования
 * PATCH sketch_design/estimate/update/{project_id}
 * @param {number} projectId - ID проекта
 * @param {Object} body - Тело обновления сметы
 * @returns {Promise<Object>} Обновленные данные сметы
 */
export const updateSketchDesignEstimate = async (projectId, body) => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/estimate/update/${projectId}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(body),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {}
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating sketch design estimate:", error);
    throw error;
  }
};

/**
 * Получить виды работ сметы ЭП
 * GET sketch_design/estimate/work_types/get/{project_id}
 */
export const getSketchDesignEstimateWorkTypes = async (projectId) => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/estimate/work_types/get/${projectId}`,
      {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "include",
      },
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(
        err.detail ? JSON.stringify(err.detail) : `HTTP ${response.status}`,
      );
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching sketch design estimate work types:", error);
    throw error;
  }
};

/**
 * Справочник статей Finansist (fact_stream) для ЭП
 * GET sketch_design/fin_job_type
 * @returns {Promise<Array<{ id: number, fact_stream_id: number, name: string }>>}
 */
export const getSketchDesignFinJobTypes = async () => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/fin_job_type`,
      {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "include",
      },
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching sketch design fin job types:", error);
    throw error;
  }
};

/**
 * Добавить вид работ в смету ЭП
 * POST sketch_design/estimate/work_types/add/{project_id}
 * На бэкенде встречаются разные имена поля в схеме и в вызове менеджера — шлём оба алиаса.
 * @param {{ name: string, work_fin_job_type_id?: number, fin_job_type?: number, is_reserve?: boolean }} body
 */
export const addSketchDesignEstimateWorkType = async (projectId, body) => {
  const articleId = Number(body.work_fin_job_type_id ?? body.fin_job_type);
  const payload = {
    ...body,
    ...(Number.isFinite(articleId) && articleId > 0
      ? { work_fin_job_type_id: articleId, fin_job_type: articleId }
      : {}),
  };
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/estimate/work_types/add/${projectId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      },
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(
        err.detail ? JSON.stringify(err.detail) : `HTTP ${response.status}`,
      );
    }
    return await response.json();
  } catch (error) {
    console.error("Error adding sketch design estimate work type:", error);
    throw error;
  }
};

/**
 * Обновить вид работ сметы ЭП
 * PATCH sketch_design/estimate/work_types/update/{work_type_id}
 */
export const updateSketchDesignEstimateWorkType = async (workTypeId, body) => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/estimate/work_types/update/${workTypeId}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(body),
      },
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(
        err.detail ? JSON.stringify(err.detail) : `HTTP ${response.status}`,
      );
    }
    return await response.json();
  } catch (error) {
    console.error("Error updating sketch design estimate work type:", error);
    throw error;
  }
};

/**
 * Удалить вид работ сметы ЭП
 * DELETE sketch_design/estimate/work_types/delete/{work_type_id}
 */
export const deleteSketchDesignEstimateWorkType = async (workTypeId) => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/estimate/work_types/delete/${workTypeId}`,
      {
        method: "DELETE",
        headers: { Accept: "application/json" },
        credentials: "include",
      },
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(
        err.detail ? JSON.stringify(err.detail) : `HTTP ${response.status}`,
      );
    }
    const text = await response.text();
    return text ? JSON.parse(text) : {};
  } catch (error) {
    console.error("Error deleting sketch design estimate work type:", error);
    throw error;
  }
};

/**
 * Получить список названий видов работ для сметы ЭП
 * GET sketch_design/estimate/work_types/names
 * @returns {Promise<Array<{ id: number, name: string, work_fin_job_type_id: number }>>}
 */
export const getSketchDesignEstimateWorkTypeNames = async () => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/estimate/work_types/names`,
      {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "include",
      },
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error(
      "Error fetching sketch design estimate work type names:",
      error,
    );
    throw error;
  }
};

/**
 * Получить этапы оплаты для коммерческого предложения эскизного проектирования
 * @param {number} projectId - ID проекта
 * @returns {Promise<Array>} Массив этапов оплаты
 */
export const getSketchDesignPaymentProcedures = async (projectId) => {
  try {
    const timestamp = new Date().getTime();
    const response = await fetch(
      `${getAPIURL()}sketch_design/commercial_offer/payment_procedure/get/${projectId}?_t=${timestamp}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching sketch design payment procedures:", error);
    throw error;
  }
};

/**
 * Обновить основные параметры КП эскизного проектирования
 * PATCH sketch_design/commercial_offer/update/{project_id}
 * @param {number} projectId - ID проекта
 * @param {{ counterparty_id?: number, receiver_id?: number, deadline_days?: number, deadline_description?: string, title?: string, nds_percent?: number }} data
 * @returns {Promise<Object>} Обновлённое КП
 */
export const updateSketchDesignCommercialOffer = async (projectId, data) => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/commercial_offer/update/${projectId}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(data),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.detail
          ? JSON.stringify(errorData.detail)
          : `HTTP ${response.status}`,
      );
    }
    return await response.json();
  } catch (error) {
    console.error("Error updating sketch design commercial offer:", error);
    throw error;
  }
};

/**
 * Создать этап оплаты для КП эскизного проектирования
 * POST sketch_design/commercial_offer/payment_procedure/add/{project_id}
 * @param {number} projectId - ID проекта
 * @param {{ stage: string, percentage_ratio: number, deadline: string }} data
 */
export const addSketchDesignPaymentProcedure = async (projectId, data) => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/commercial_offer/payment_procedure/add/${projectId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(data),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.detail
          ? JSON.stringify(errorData.detail)
          : `HTTP ${response.status}`,
      );
    }
    return await response.json();
  } catch (error) {
    console.error("Error adding sketch design payment procedure:", error);
    throw error;
  }
};

/**
 * Обновить процент этапа оплаты
 * PATCH sketch_design/commercial_offer/payment_procedure/update/{project_id}
 * @param {number} projectId - ID проекта
 * @param {{ payment_procedure_id: number, percentage_ratio: number }} data
 */
export const updateSketchDesignPaymentProcedure = async (projectId, data) => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/commercial_offer/payment_procedure/update/${projectId}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(data),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.detail
          ? JSON.stringify(errorData.detail)
          : `HTTP ${response.status}`,
      );
    }
    return await response.json();
  } catch (error) {
    console.error("Error updating sketch design payment procedure:", error);
    throw error;
  }
};

/**
 * Удалить этап оплаты
 * DELETE sketch_design/commercial_offer/payment_procedure/delete/{payment_procedure_id}
 * @param {number} paymentProcedureId - ID этапа оплаты
 */
export const deleteSketchDesignPaymentProcedure = async (
  paymentProcedureId,
) => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/commercial_offer/payment_procedure/delete/${paymentProcedureId}`,
      {
        method: "DELETE",
        headers: { Accept: "application/json" },
        credentials: "include",
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.detail
          ? JSON.stringify(errorData.detail)
          : `HTTP ${response.status}`,
      );
    }
    return await response
      .text()
      .then((t) => (t ? JSON.parse(t) : null))
      .catch(() => null);
  } catch (error) {
    console.error("Error deleting sketch design payment procedure:", error);
    throw error;
  }
};

/**
 * Получить статус согласования по проекту ЭП и разделу
 * GET sketch_design/approvals/get/{project_id}/{chapter}
 * @param {number} projectId - ID проекта ЭП
 * @param {string} chapter - Раздел: "commercial_offer" | "estimate"
 * @returns {Promise<Object|null>} Данные согласования или null, если не найдено / ошибка
 */
export const getSketchDesignApproval = async (projectId, chapter) => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/approvals/get/${projectId}/${chapter}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
      },
    );
    if (response.status === 422 || response.status === 404) {
      return null;
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.detail
          ? JSON.stringify(errorData.detail)
          : `HTTP ${response.status}`,
      );
    }
    return await response.json();
  } catch (error) {
    console.error("Ошибка при получении согласования ЭП:", error);
    return null;
  }
};

/**
 * Создать согласование по проекту ЭП и разделу
 * POST sketch_design/approvals/create/{project_id}/{chapter}
 * @param {number} projectId - ID проекта ЭП
 * @param {string} chapter - Раздел: "commercial_offer" | "estimate"
 * @param {number[]} approverUserIds - Массив ID пользователей-согласующих
 * @returns {Promise<Object>} Созданное согласование
 */
export const createSketchDesignApproval = async (
  projectId,
  chapter,
  approverUserIds,
) => {
  const response = await fetch(
    `${getAPIURL()}sketch_design/approvals/create/${projectId}/${chapter}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
      body: JSON.stringify(approverUserIds),
    },
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail
        ? JSON.stringify(errorData.detail)
        : `HTTP ${response.status}`,
    );
  }
  return response.json();
};

/**
 * Удалить согласование по проекту ЭП и разделу
 * DELETE sketch_design/approvals/delete/{project_id}/{chapter}
 * @param {number} projectId - ID проекта ЭП
 * @param {string} chapter - Раздел: "commercial_offer" | "estimate"
 */
export const deleteSketchDesignApproval = async (projectId, chapter) => {
  const response = await fetch(
    `${getAPIURL()}sketch_design/approvals/delete/${projectId}/${chapter}`,
    {
      method: "DELETE",
      headers: { Accept: "application/json" },
      credentials: "include",
    },
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail
        ? JSON.stringify(errorData.detail)
        : `HTTP ${response.status}`,
    );
  }
  return response.json().catch(() => null);
};

/**
 * Обновить статус согласующего
 * PATCH sketch_design/approvals/approver/status/{approver_id}/{status}
 * @param {number} approverId - ID записи согласующего (approver.id)
 * @param {string} status - approved | in_processing | rejected
 */
export const updateSketchDesignApproverStatus = async (approverId, status) => {
  const response = await fetch(
    `${getAPIURL()}sketch_design/approvals/approver/status/${approverId}/${status}`,
    {
      method: "PATCH",
      headers: { Accept: "application/json" },
      credentials: "include",
    },
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail
        ? JSON.stringify(errorData.detail)
        : `HTTP ${response.status}`,
    );
  }
  return response.json();
};

/**
 * Получить смету по договору с фактом
 * GET /fin_plan/projects/{project_id}/with_fact
 * Смета по договору плюс факт: трудозатраты по блокам, по разделам, суммарная фактическая стоимость и сумма сметы по договору.
 * @param {number} projectId - ID проекта
 * @param {string|null} dateFrom - Начало периода для факта (дата отчёта), формат YYYY-MM-DD
 * @param {string|null} dateTo - Конец периода для факта (дата отчёта), формат YYYY-MM-DD
 * @returns {Promise<Object>} Данные сметы с фактом
 */
export const getFinPlanWithFact = async (
  projectId,
  dateFrom = null,
  dateTo = null,
) => {
  try {
    const params = new URLSearchParams();
    if (dateFrom) {
      params.append("date_from", dateFrom);
    }
    if (dateTo) {
      params.append("date_to", dateTo);
    }

    const queryString = params.toString();
    const url = `${getAPIURL()}fin_plan/projects/${projectId}/with_fact${queryString ? `?${queryString}` : ""}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {}
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching fin plan with fact:", error);
    throw error;
  }
};

/**
 * Получить данные по блокам одного раздела с фактом
 * GET /fin_plan/projects/{project_id}/sections/{section_id}/blocks
 * Данные по блокам одного раздела: смета и факт по каждому блоку.
 * @param {number} projectId - ID проекта
 * @param {number} sectionId - ID раздела
 * @param {string|null} dateFrom - Начало периода для факта (дата отчёта), формат YYYY-MM-DD
 * @param {string|null} dateTo - Конец периода для факта (дата отчёта), формат YYYY-MM-DD
 * @returns {Promise<Object>} Данные по блокам раздела с фактом
 */
export const getFinPlanSectionBlocks = async (
  projectId,
  sectionId,
  dateFrom = null,
  dateTo = null,
) => {
  try {
    const params = new URLSearchParams();
    if (dateFrom) {
      params.append("date_from", dateFrom);
    }
    if (dateTo) {
      params.append("date_to", dateTo);
    }

    const queryString = params.toString();
    const url = `${getAPIURL()}fin_plan/projects/${projectId}/sections/${sectionId}/blocks${queryString ? `?${queryString}` : ""}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {}
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching fin plan section blocks:", error);
    throw error;
  }
};
